import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { useTRPC } from '../../hooks/useTRPC';

import { captureAndReportError } from '../../utils/errorReporting';
import { LoadingIcon } from '../LoadingIcon';
import { VerificationCodeInput } from '../VerificationCodeInput';

interface ProfileFormData {
  email: string;
  fullName: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

export const ProfileSettingsPage = () => {
  const [authState, { refreshUser }] = useAuth();
  const trpc = useTRPC();
  const [formData, setFormData] = useState<ProfileFormData>({
    email: '',
    fullName: '',
    password: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    if (authState.type === 'Authenticated') {
      setFormData({
        email: authState.user.email,
        fullName: authState.user.fullName,
        password: '',
        confirmPassword: '',
      });
    }
  }, [authState]);

  useEffect(() => {
    if (authState.type === 'Authenticated') {
      const fullNameChanged = formData.fullName !== authState.user.fullName;
      const passwordEntered =
        formData.password.length > 0 || formData.confirmPassword.length > 0;
      setHasChanges(fullNameChanged || passwordEntered);
    }
  }, [formData, authState]);

  const handleInputChange =
    (field: keyof ProfileFormData) => (value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      if (field === 'password' || field === 'confirmPassword') {
        setFormErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const validatePasswordFields = () => {
    const errors: FormErrors = {};

    if (formData.password || formData.confirmPassword) {
      if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters long';
      }

      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!hasChanges) {
      return;
    }

    setIsLoading(true);

    try {
      if (formData.password || formData.confirmPassword) {
        if (!validatePasswordFields()) {
          setIsLoading(false);
          return;
        }

        await trpc.initiatePasswordChange.mutate({
          newPassword: formData.password,
          confirmPassword: formData.confirmPassword,
        });

        setIsVerificationModalOpen(true);
        setIsLoading(false);
        return;
      }

      if (
        authState.type === 'Authenticated' &&
        formData.fullName !== authState.user.fullName
      ) {
        await trpc.updateUser.mutate({ fullName: formData.fullName });

        await refreshUser();

        toast.success('Profile updated successfully');
      } else {
        toast.info('No changes to save');
      }
    } catch (error: unknown) {
      captureAndReportError('Failed to save profile:', error);

      if (error instanceof Error) {
        toast.error(error.message);
      } else if (typeof error === 'object' && error && 'message' in error) {
        toast.error(String((error as { message: unknown }).message));
      } else {
        toast.error('Failed to update profile. Please try again.');
      }
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleVerifyPasswordChange = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit verification code');
      return;
    }

    setIsLoading(true);

    try {
      await trpc.verifyPasswordChange.mutate({
        code: verificationCode,
      });

      setIsVerificationModalOpen(false);

      setFormData((prev) => ({ ...prev, password: '', confirmPassword: '' }));
      setVerificationCode('');

      await refreshUser();

      toast.success('Password changed successfully');
    } catch (error: unknown) {
      captureAndReportError('Failed to verify password change:', error);

      if (error instanceof Error) {
        toast.error(error.message);
      } else if (typeof error === 'object' && error && 'message' in error) {
        toast.error(String((error as { message: unknown }).message));
      } else {
        toast.error('Failed to verify password change. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && hasChanges) {
      handleSave();
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="bg-neutral-900 h-[72px] flex-shrink-0 sticky top-0 z-40">
        <div className="h-full flex items-center justify-between px-10">
          <div className="flex items-center gap-2">
            <PersonIcon className="size-5 fill-white" />
            <span className="text-white font-medium text-base">Profile</span>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
            className={`relative bg-teal-600 text-white font-medium px-6 py-2 rounded-lg transition-all duration-300 min-w-[120px] ${
              !hasChanges || isLoading ? 'opacity-50' : 'hover:bg-teal-700'
            }`}
            style={{
              cursor: !hasChanges || isLoading ? 'default' : 'pointer',
            }}
          >
            <span
              className={`inline-flex items-center justify-center transition-opacity duration-300 ${
                isLoading ? 'opacity-0' : 'opacity-100'
              }`}
            >
              Save Changes
            </span>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <LoadingIcon
                  label="Loading"
                  className="size-5 text-white transition-opacity duration-300"
                />
              </div>
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-10">
          <div className="max-w-2xl mx-auto space-y-12">
            <div className="space-y-6">
              <div className="space-y-1">
                <label className="block text-sm font-bold text-white">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    aria-label="Email Address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email')(e.target.value)}
                    className="w-full h-10 px-4 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    readOnly
                    disabled={isLoading}
                  />
                </div>
                {authState.type === 'Authenticated' && (
                  <div
                    className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-medium ${
                      authState.user.emailVerified
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                        : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                    }`}
                  >
                    {authState.user.emailVerified ? (
                      <svg
                        className="size-3.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <title>Verified</title>
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="size-3.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <title>Unverified</title>
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {authState.user.emailVerified ? 'Verified' : 'Unverified'}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-bold text-white">
                  Full Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    aria-label="Full Name"
                    value={formData.fullName}
                    onChange={(e) =>
                      handleInputChange('fullName')(e.target.value)
                    }
                    className="w-full h-10 px-4 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    disabled={isLoading}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-bold text-white">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    aria-label="Password"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange('password')(e.target.value)
                    }
                    className={`w-full h-10 px-4 pr-12 bg-neutral-800 border ${
                      formErrors.password
                        ? 'border-red-500'
                        : 'border-neutral-700'
                    } rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 ${
                      formErrors.password
                        ? 'focus:ring-red-500'
                        : 'focus:ring-teal-500'
                    } focus:border-transparent`}
                    placeholder="••••••••"
                    disabled={isLoading}
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
                  >
                    <svg
                      className="size-4 fill-current"
                      viewBox="0 0 16 16"
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      <title>
                        {showPassword ? 'Hide password' : 'Show password'}
                      </title>
                      {showPassword ? (
                        <>
                          <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
                          <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                          <path d="m3.35 5.47-.818-.82A5.944 5.944 0 0 0 8 2.5c-2.12 0-3.879 1.168-5.168 2.457A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
                        </>
                      ) : (
                        <>
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {formErrors.password && (
                  <p className="text-sm text-red-500 mt-1">
                    {formErrors.password}
                  </p>
                )}
              </div>

              {(formData.password || formData.confirmPassword) && (
                <div className="space-y-1">
                  <label className="block text-sm font-bold text-white">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      aria-label="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleInputChange('confirmPassword')(e.target.value)
                      }
                      className={`w-full h-10 px-4 pr-12 bg-neutral-800 border ${
                        formErrors.confirmPassword
                          ? 'border-red-500'
                          : 'border-neutral-700'
                      } rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 ${
                        formErrors.confirmPassword
                          ? 'focus:ring-red-500'
                          : 'focus:ring-teal-500'
                      } focus:border-transparent`}
                      placeholder="••••••••"
                      disabled={isLoading}
                      onKeyDown={handleKeyDown}
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
                    >
                      <svg
                        className="size-4 fill-current"
                        viewBox="0 0 16 16"
                        aria-label={
                          showConfirmPassword
                            ? 'Hide password'
                            : 'Show password'
                        }
                      >
                        <title>
                          {showConfirmPassword
                            ? 'Hide password'
                            : 'Show password'}
                        </title>
                        {showConfirmPassword ? (
                          <>
                            <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
                            <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                            <path d="m3.35 5.47-.818-.82A5.944 5.944 0 0 0 8 2.5c-2.12 0-3.879 1.168-5.168 2.457A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
                          </>
                        ) : (
                          <>
                            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  {formErrors.confirmPassword && (
                    <p className="text-sm text-red-500 mt-1">
                      {formErrors.confirmPassword}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isVerificationModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-4">
              Verify Password Change
            </h2>
            <p className="text-neutral-400 mb-6">
              We've sent a verification code to your email address. Please enter
              the 6-digit code below to confirm your password change.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-white mb-2">
                  Verification Code
                </label>
                <VerificationCodeInput
                  value={verificationCode}
                  onChange={setVerificationCode}
                  onEnter={() => {
                    if (verificationCode.length === 6) {
                      handleVerifyPasswordChange();
                    }
                  }}
                  className="justify-start"
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsVerificationModalOpen(false);
                    setVerificationCode('');
                  }}
                  disabled={isLoading}
                  className="flex-1 bg-neutral-800 border border-neutral-700 rounded-xl px-6 py-3 text-sm font-bold text-white hover:bg-neutral-700 disabled:opacity-50 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleVerifyPasswordChange}
                  disabled={isLoading || verificationCode.length !== 6}
                  className="flex-1 bg-gradient-to-b from-teal-500 via-teal-600 to-teal-700 border border-neutral-600 rounded-xl px-6 py-3 text-sm font-bold text-white hover:from-teal-400 hover:via-teal-500 hover:to-teal-600 disabled:opacity-50 disabled:from-neutral-600 disabled:via-neutral-700 disabled:to-neutral-800 transition-all duration-300"
                >
                  {isLoading ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
